/**
 * 請求計上日 / 払い計上日のカスケード関数（FE 側、BE の RecordingDateService と同型）。
 *
 * 3 段カスケード（手入力最優先）:
 * 1. 案件個別の手入力値（manualValue）
 * 2. 取引先（Customer / CONTRACTOR Partner）の個別設定（null = 拠点に従う）
 * 3. 拠点（IN_HOUSE Partner）のデフォルト設定（NOT NULL）
 *
 * 計上日の値は ShipmentOrderGroup.shippingDate / deliveryDate のいずれかを返す。
 *
 * BE: logi-go-api/src/shared/modules/recording-date/recording-date.service.ts
 * 設計書: docs/change-designs/issues/1846-recording-date/10-design.md §2.1 / CF-5
 *
 * 用途（B-1 FE 4 モーダル DatePicker）:
 * - 新規作成モーダルで荷主/Partner 選択時に初期値計算するために使用
 * - 編集モーダルでは DB の既存値をそのまま表示するため呼ばない
 */

/**
 * 計上基準（発日 / 着日）。aspida 生成型 (`'SHIPPING_DATE' | 'DELIVERY_DATE'`) と互換。
 */
export type RecordingDateType = 'SHIPPING_DATE' | 'DELIVERY_DATE'

/**
 * 計上基準のカスケード解決のみを行う（Date は返さない、type のみ）。
 *
 * - 取引先が明示設定されていればその値を返す
 * - 未設定（null）なら拠点デフォルトを返す
 */
export const resolveIncomeRecordingDateType = (input: {
  customer: { incomeRecordingDateType: RecordingDateType | null }
  inHousePartner: { defaultIncomeRecordingDateType: RecordingDateType }
}): RecordingDateType =>
  input.customer.incomeRecordingDateType ??
  input.inHousePartner.defaultIncomeRecordingDateType

/**
 * 払い計上基準のカスケード解決のみを行う（Date は返さない、type のみ）。
 */
export const resolveExpenseRecordingDateType = (input: {
  contractorPartner: { expenseRecordingDateType: RecordingDateType | null }
  inHousePartner: { defaultExpenseRecordingDateType: RecordingDateType }
}): RecordingDateType =>
  input.contractorPartner.expenseRecordingDateType ??
  input.inHousePartner.defaultExpenseRecordingDateType

/**
 * 請求計上日（incomeRecordingDate）を 3 段カスケードで決定する。
 *
 * - 手入力値が指定されていればそれを返す（最優先）
 * - 荷主 (Customer) の incomeRecordingDateType を採用（null なら拠点デフォルト）
 * - 採用された type に応じて group.shippingDate or group.deliveryDate を返す
 */
export const calculateIncomeRecordingDate = (input: {
  manualValue?: Date | null
  customer: { incomeRecordingDateType: RecordingDateType | null }
  inHousePartner: { defaultIncomeRecordingDateType: RecordingDateType }
  group: { shippingDate: Date; deliveryDate: Date }
}): Date => {
  if (input.manualValue) return input.manualValue
  const type = resolveIncomeRecordingDateType({
    customer: input.customer,
    inHousePartner: input.inHousePartner
  })
  return type === 'DELIVERY_DATE'
    ? input.group.deliveryDate
    : input.group.shippingDate
}

/**
 * 払い計上日（expenseRecordingDate）を 3 段カスケードで決定する。
 *
 * - 手入力値が指定されていればそれを返す（最優先）
 * - 協力会社 (CONTRACTOR Partner) の expenseRecordingDateType を採用（null なら拠点デフォルト）
 * - 採用された type に応じて group.shippingDate or group.deliveryDate を返す
 */
export const calculateExpenseRecordingDate = (input: {
  manualValue?: Date | null
  contractorPartner: { expenseRecordingDateType: RecordingDateType | null }
  inHousePartner: { defaultExpenseRecordingDateType: RecordingDateType }
  group: { shippingDate: Date; deliveryDate: Date }
}): Date => {
  if (input.manualValue) return input.manualValue
  const type = resolveExpenseRecordingDateType({
    contractorPartner: input.contractorPartner,
    inHousePartner: input.inHousePartner
  })
  return type === 'DELIVERY_DATE'
    ? input.group.deliveryDate
    : input.group.shippingDate
}
